import React, { useEffect, useMemo, useState } from "react";
import { Calculator, Plus, RefreshCw, Save, Trash2, X } from "lucide-react";

import api from "../api";
import { useMasterData } from "../../utils/useMasterData";
import InvoiceCreate from "./InvoiceCreate";
import InvoiceDetail from "./InvoiceDetail";
import InvoiceList from "./InvoiceList";
import GSTReports from "./GSTReports";

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const EMPTY_CONFIG = {
  gstin: "",
  legal_name: "",
  trade_name: "",
  state_code: "",
  state_name: "",
  address: "",
  pan: "",
  registration_type: "REGULAR",
  is_gst_enabled: true,
};

const EMPTY_HSN = {
  hsn_code: "",
  description: "",
  product_category: "GOLD_JEWELLERY",
  cgst_rate: 0,
  sgst_rate: 0,
  igst_rate: 0,
  is_exempt: false,
};

function GSTSetup() {
  const { getValues } = useMasterData(["GST_REGISTRATION", "PRODUCT_CATEGORY_GST"]);
  const registrationTypes = getValues("GST_REGISTRATION");
  const productCategories = getValues("PRODUCT_CATEGORY_GST");
  const [activeTab, setActiveTab] = useState("setup");
  const [invoiceView, setInvoiceView] = useState({ mode: "list", id: null, seed: null });
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [configured, setConfigured] = useState(false);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerDrafts, setCustomerDrafts] = useState({});
  const [hsnForm, setHsnForm] = useState(EMPTY_HSN);
  const [editingHsnId, setEditingHsnId] = useState(null);
  const [showHsnModal, setShowHsnModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState({
    product_category: "GOLD_JEWELLERY",
    customer_state_code: "",
    rates: null,
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [configRes, hsnRes, customerRes] = await Promise.all([
        api.get("/accounts/gst/config"),
        api.get("/accounts/gst/hsn-codes"),
        api.get("/accounts/gst/customers"),
      ]);
      if (configRes.data.configured) {
        setConfig({ ...EMPTY_CONFIG, ...configRes.data, is_gst_enabled: Boolean(configRes.data.is_gst_enabled) });
        setConfigured(true);
      } else {
        setConfig(EMPTY_CONFIG);
        setConfigured(false);
      }
      setHsnCodes(hsnRes.data.data || []);
      const rows = customerRes.data.data || [];
      setCustomers(rows);
      setCustomerDrafts(
        rows.reduce((acc, customer) => {
          acc[customer.id] = {
            gstin: customer.gstin || "",
            gst_registered: Boolean(customer.gst_registered),
            state_code: customer.state_code || "",
            pan: customer.pan || "",
          };
          return acc;
        }, {})
      );
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load GST setup.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        const res = await api.get(
          `/accounts/gst/tax-preview?product_category=${preview.product_category}&customer_state_code=${preview.customer_state_code || ""}`
        );
        setPreview((prev) => ({ ...prev, rates: res.data }));
      } catch {
        setPreview((prev) => ({ ...prev, rates: null }));
      }
    };
    run();
  }, [preview.product_category, preview.customer_state_code]);

  const updateConfigField = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const saveConfig = async () => {
    setError("");
    setMessage("");
    if (!config.gstin.trim()) {
      setError("GSTIN is required.");
      return;
    }
    if (config.gstin.trim() && !GSTIN_REGEX.test(config.gstin.trim().toUpperCase())) {
      setError("GSTIN format is invalid.");
      return;
    }
    if (!config.legal_name.trim() || !config.state_code.trim()) {
      setError("Legal name and state code are required.");
      return;
    }
    try {
      const payload = {
        ...config,
        gstin: config.gstin.trim().toUpperCase(),
        pan: config.pan.trim().toUpperCase(),
      };
      const res = await api.post("/accounts/gst/setup", payload);
      setMessage(
        res.data.seeded_hsn
          ? `GST configuration saved. Seeded ${res.data.seeded_hsn} default HSN code(s).`
          : "GST configuration saved."
      );
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save GST configuration.");
    }
  };

  const openAddHsn = () => {
    setEditingHsnId(null);
    setHsnForm(EMPTY_HSN);
    setShowHsnModal(true);
  };

  const openEditHsn = (hsn) => {
    setEditingHsnId(hsn.id);
    setHsnForm({
      hsn_code: hsn.hsn_code || "",
      description: hsn.description || "",
      product_category: hsn.product_category || "GOLD_JEWELLERY",
      cgst_rate: hsn.cgst_rate ?? 0,
      sgst_rate: hsn.sgst_rate ?? 0,
      igst_rate: hsn.igst_rate ?? 0,
      is_exempt: Boolean(hsn.is_exempt),
    });
    setShowHsnModal(true);
  };

  const saveHsn = async () => {
    try {
      const payload = {
        ...hsnForm,
        hsn_code: hsnForm.hsn_code.trim().toUpperCase(),
        description: hsnForm.description.trim(),
        product_category: hsnForm.product_category.toUpperCase(),
        cgst_rate: Number(hsnForm.cgst_rate || 0),
        sgst_rate: Number(hsnForm.sgst_rate || 0),
        igst_rate: Number(hsnForm.igst_rate || 0),
      };
      if (editingHsnId) {
        await api.put(`/accounts/gst/hsn-codes/${editingHsnId}`, payload);
      } else {
        await api.post("/accounts/gst/hsn-codes", payload);
      }
      setShowHsnModal(false);
      setMessage(`HSN code ${editingHsnId ? "updated" : "created"} successfully.`);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save HSN code.");
    }
  };

  const deleteHsn = async (id) => {
    try {
      await api.delete(`/accounts/gst/hsn-codes/${id}`);
      setMessage("HSN code deleted.");
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete HSN code.");
    }
  };

  const loadDefaults = async () => {
    try {
      const res = await api.post("/accounts/gst/hsn-codes/seed-defaults");
      setMessage(`Loaded ${res.data.seeded || 0} default HSN code(s).`);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load default HSN codes.");
    }
  };

  const updateCustomerDraft = (customerId, field, value) => {
    setCustomerDrafts((prev) => ({
      ...prev,
      [customerId]: {
        ...prev[customerId],
        [field]: value,
      },
    }));
  };

  const saveCustomer = async (customerId) => {
    try {
      const res = await api.patch(`/accounts/gst/customers/${customerId}`, customerDrafts[customerId]);
      const updated = res.data.data;
      setCustomers((prev) => prev.map((row) => (row.id === customerId ? updated : row)));
      setCustomerDrafts((prev) => ({
        ...prev,
        [customerId]: {
          gstin: updated.gstin || "",
          gst_registered: Boolean(updated.gst_registered),
          state_code: updated.state_code || "",
          pan: updated.pan || "",
        },
      }));
      setMessage(`Updated GST details for ${updated.name}.`);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update customer GST details.");
    }
  };

  const previewDescription = useMemo(() => {
    if (!preview.rates) return "Select a category and state code to preview tax.";
    if (preview.rates.is_exempt) return `HSN ${preview.rates.hsn_code || "-"} is exempt.`;
    return `HSN ${preview.rates.hsn_code || "-"} | ${preview.rates.supply_type} supply`;
  }, [preview.rates]);

  if (loading) {
    return <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">Loading GST workspace...</div>;
  }

  return (
    <div className="space-y-6">
      {(message || error) && (
        <div className={`rounded-2xl border px-4 py-3 text-sm ${error ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
          {error || message}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setActiveTab("setup")} className={`tab-btn ${activeTab === "setup" ? "tab-btn-active" : ""}`}>GST Setup</button>
        <button onClick={() => setActiveTab("invoices")} className={`tab-btn ${activeTab === "invoices" ? "tab-btn-active" : ""}`}>Invoices</button>
        <button onClick={() => setActiveTab("reports")} className={`tab-btn ${activeTab === "reports" ? "tab-btn-active" : ""}`}>GST Reports</button>
      </div>

      {activeTab === "setup" && (
        <>
          <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Organization GST Details</h3>
                <p className="text-sm text-slate-500">GSTIN, legal name, trade name, state code, PAN, registration type, and master GST toggle.</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {configured ? "Configured" : "Pending Setup"}
              </span>
            </div>

            <div className="grid gap-4 p-6 md:grid-cols-2 xl:grid-cols-3">
              <Field label="GSTIN">
                <input value={config.gstin} onChange={(e) => updateConfigField("gstin", e.target.value.toUpperCase())} className="input" placeholder="36AABCZ1234F1Z5" />
              </Field>
              <Field label="Legal Name*">
                <input value={config.legal_name} onChange={(e) => updateConfigField("legal_name", e.target.value)} className="input" />
              </Field>
              <Field label="Trade Name">
                <input value={config.trade_name} onChange={(e) => updateConfigField("trade_name", e.target.value)} className="input" />
              </Field>
              <Field label="State Code*">
                <input value={config.state_code} onChange={(e) => updateConfigField("state_code", e.target.value)} className="input" placeholder="36" />
              </Field>
              <Field label="State Name">
                <input value={config.state_name} onChange={(e) => updateConfigField("state_name", e.target.value)} className="input" />
              </Field>
              <Field label="PAN">
                <input value={config.pan} onChange={(e) => updateConfigField("pan", e.target.value.toUpperCase())} className="input" />
              </Field>
              <Field label="Registration Type">
                <select value={config.registration_type} onChange={(e) => updateConfigField("registration_type", e.target.value)} className="input">
                  {registrationTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
              </Field>
              <Field label="GST Enabled">
                <label className="toggle-card">
                  <input type="checkbox" checked={config.is_gst_enabled} onChange={(e) => updateConfigField("is_gst_enabled", e.target.checked)} />
                  <span>Enable GST calculations across invoices</span>
                </label>
              </Field>
              <Field label="Registered Address" className="md:col-span-2 xl:col-span-3">
                <textarea value={config.address} onChange={(e) => updateConfigField("address", e.target.value)} className="input min-h-[100px]" />
              </Field>
            </div>

            <div className="border-t border-slate-100 px-6 py-4">
              <button onClick={saveConfig} className="primary-btn"><Save size={15} /> Save GST Config</button>
            </div>
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">HSN Code Management</h3>
                  <p className="text-sm text-slate-500">Jewellery defaults plus custom organization-level HSN codes.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={loadDefaults} className="secondary-btn"><RefreshCw size={15} /> Load Default HSN Codes</button>
                  <button onClick={openAddHsn} className="primary-btn"><Plus size={15} /> Add HSN</button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Code</th>
                      <th className="px-4 py-3">Description</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">CGST%</th>
                      <th className="px-4 py-3">SGST%</th>
                      <th className="px-4 py-3">IGST%</th>
                      <th className="px-4 py-3">Exempt</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hsnCodes.map((item) => (
                      <tr key={item.id} className="border-t border-slate-100">
                        <td className="px-4 py-3 font-semibold text-indigo-700">{item.hsn_code}</td>
                        <td className="px-4 py-3">{item.description}</td>
                        <td className="px-4 py-3">{item.product_category}</td>
                        <td className="px-4 py-3">{item.cgst_rate}</td>
                        <td className="px-4 py-3">{item.sgst_rate}</td>
                        <td className="px-4 py-3">{item.igst_rate}</td>
                        <td className="px-4 py-3">{item.is_exempt ? "Yes" : "No"}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => openEditHsn(item)} className="table-btn">Edit</button>
                            <button onClick={() => deleteHsn(item.id)} className="table-btn text-rose-600"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-6 py-5">
                <h3 className="text-xl font-bold text-slate-900">Auto Tax Preview</h3>
                <p className="text-sm text-slate-500">Preview CGST/SGST or IGST by product category and destination state.</p>
              </div>
              <div className="space-y-4 p-6">
                <Field label="Product Category">
                  <select value={preview.product_category} onChange={(e) => setPreview((prev) => ({ ...prev, product_category: e.target.value }))} className="input">
                    {productCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                  </select>
                </Field>
                <Field label="Customer State Code">
                  <input value={preview.customer_state_code} onChange={(e) => setPreview((prev) => ({ ...prev, customer_state_code: e.target.value }))} className="input" placeholder="36" />
                </Field>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="mb-2 flex items-center gap-2 text-slate-800"><Calculator size={16} /> <span className="font-semibold">Tax Output</span></div>
                  <div className="text-sm text-slate-600">{previewDescription}</div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <PreviewBox label="CGST" value={preview.rates?.cgst || 0} />
                    <PreviewBox label="SGST" value={preview.rates?.sgst || 0} />
                    <PreviewBox label="IGST" value={preview.rates?.igst || 0} />
                    <PreviewBox label="Supply Type" value={preview.rates?.supply_type || "-"} />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-6 py-5">
              <h3 className="text-xl font-bold text-slate-900">Customer GST Status</h3>
              <p className="text-sm text-slate-500">Inline GSTIN update, GST registration toggle, state code, PAN, and supply type preview.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">GSTIN</th>
                    <th className="px-4 py-3">GST Status</th>
                    <th className="px-4 py-3">State Code</th>
                    <th className="px-4 py-3">PAN</th>
                    <th className="px-4 py-3">Supply Type</th>
                    <th className="px-4 py-3 text-right">Save</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => {
                    const draft = customerDrafts[customer.id] || {};
                    return (
                      <tr key={customer.id} className="border-t border-slate-100">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900">{customer.name}</div>
                          <div className="text-xs text-slate-500">{customer.code}</div>
                        </td>
                        <td className="px-4 py-3"><input value={draft.gstin || ""} onChange={(e) => updateCustomerDraft(customer.id, "gstin", e.target.value.toUpperCase())} className="input" /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${customer.gst_registered ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                              {customer.gst_registered ? "GST Registered" : "Non-GST"}
                            </span>
                            <input type="checkbox" checked={Boolean(draft.gst_registered)} onChange={(e) => updateCustomerDraft(customer.id, "gst_registered", e.target.checked)} />
                          </div>
                        </td>
                        <td className="px-4 py-3"><input value={draft.state_code || ""} onChange={(e) => updateCustomerDraft(customer.id, "state_code", e.target.value)} className="input" /></td>
                        <td className="px-4 py-3"><input value={draft.pan || ""} onChange={(e) => updateCustomerDraft(customer.id, "pan", e.target.value.toUpperCase())} className="input" /></td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${customer.supply_type === "INTER" ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-700"}`}>
                            {customer.supply_type === "INTER" ? "INTER-STATE" : "INTRA-STATE"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right"><button onClick={() => saveCustomer(customer.id)} className="primary-btn">Save</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {activeTab === "invoices" && (
        <>
          {invoiceView.mode === "list" && (
            <InvoiceList
              onCreate={() => setInvoiceView({ mode: "create", id: null, seed: null })}
              onView={(id) => setInvoiceView({ mode: "detail", id, seed: null })}
              onCreateFromQuotation={(seed) => setInvoiceView({ mode: "create", id: null, seed })}
            />
          )}
          {invoiceView.mode === "create" && (
            <InvoiceCreate
              onCancel={() => setInvoiceView({ mode: "list", id: null, seed: null })}
              onCreated={({ id, mode }) => setInvoiceView({ mode, id: mode === "detail" ? id : null, seed: null })}
              initialData={invoiceView.seed || {}}
            />
          )}
          {invoiceView.mode === "detail" && (
            <InvoiceDetail
              invoiceId={invoiceView.id}
              onBack={() => setInvoiceView({ mode: "list", id: null, seed: null })}
            />
          )}
        </>
      )}

      {activeTab === "reports" && <GSTReports />}

      {showHsnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h4 className="text-lg font-bold text-slate-900">{editingHsnId ? "Edit HSN Code" : "Add HSN Code"}</h4>
              <button onClick={() => setShowHsnModal(false)} className="rounded-full p-2 hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="grid gap-4 p-6 md:grid-cols-2">
              <Field label="HSN Code"><input value={hsnForm.hsn_code} onChange={(e) => setHsnForm((prev) => ({ ...prev, hsn_code: e.target.value }))} className="input" /></Field>
              <Field label="Category">
                <select value={hsnForm.product_category} onChange={(e) => setHsnForm((prev) => ({ ...prev, product_category: e.target.value }))} className="input">
                  {productCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
              </Field>
              <Field label="Description" className="md:col-span-2"><input value={hsnForm.description} onChange={(e) => setHsnForm((prev) => ({ ...prev, description: e.target.value }))} className="input" /></Field>
              <Field label="CGST %"><input type="number" value={hsnForm.cgst_rate} onChange={(e) => setHsnForm((prev) => ({ ...prev, cgst_rate: e.target.value }))} className="input" /></Field>
              <Field label="SGST %"><input type="number" value={hsnForm.sgst_rate} onChange={(e) => setHsnForm((prev) => ({ ...prev, sgst_rate: e.target.value }))} className="input" /></Field>
              <Field label="IGST %"><input type="number" value={hsnForm.igst_rate} onChange={(e) => setHsnForm((prev) => ({ ...prev, igst_rate: e.target.value }))} className="input" /></Field>
              <Field label="Exempt">
                <label className="toggle-card">
                  <input type="checkbox" checked={hsnForm.is_exempt} onChange={(e) => setHsnForm((prev) => ({ ...prev, is_exempt: e.target.checked }))} />
                  <span>This HSN is exempt</span>
                </label>
              </Field>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
              <button onClick={() => setShowHsnModal(false)} className="secondary-btn">Cancel</button>
              <button onClick={saveHsn} className="primary-btn">Save HSN</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .primary-btn, .secondary-btn, .table-btn, .tab-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
        }
        .primary-btn { background: rgb(79 70 229); color: white; }
        .secondary-btn, .table-btn, .tab-btn { border: 1px solid rgb(203 213 225); background: white; color: rgb(51 65 85); }
        .tab-btn-active { background: rgb(79 70 229); color: white; border-color: rgb(79 70 229); }
        .toggle-card {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          min-height: 44px;
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function PreviewBox({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

export default GSTSetup;
